import { useMemo } from 'react';
import { useTier } from './useTier';
import { useAuth } from '../context/AuthContext';

/**
 * useChatPermissions - Hook for checking chat-related permissions
 *
 * Provides tier-based access control for:
 * - Direct messages
 * - Meeting chat persistence
 * - Group chats (with limits)
 * - Organization channels
 * - File sharing
 * - Search
 */

export function useChatPermissions() {
  const { tier, hasFeature, checkLimit, isTeam, hasOrganization, organizationId } = useTier();
  const { user, userProfile } = useAuth();

  const permissions = useMemo(() => {
    // Feature availability
    const canUseDMs = hasFeature('directMessages');
    const canUseMeetingChat = hasFeature('meetingChat');
    const hasPersistentMeetingChat = hasFeature('persistentMeetingChat');
    const canUseGroupChats = hasFeature('groupChats');
    const canUseOrgChannels = hasFeature('orgChannels') && hasOrganization;
    const canShareFiles = hasFeature('chatFileSharing');
    const canSearchChats = hasFeature('chatSearch');

    // Allowed file types for uploads
    const ALLOWED_FILE_TYPES = [
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
      'video/mp4',
      'video/webm',
      'video/quicktime',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];

    // Get limits
    const getLimits = () => {
      const tierLimits = {
        free: {
          chatHistoryDays: 7,
          activeGroupChats: 0,
          chatFileSize: 5, // MB
          meetingChatPersistent: false
        },
        pro: {
          chatHistoryDays: 90,
          activeGroupChats: 5,
          chatFileSize: 100,
          meetingChatPersistent: true
        },
        team: {
          chatHistoryDays: -1, // unlimited
          activeGroupChats: -1, // unlimited
          chatFileSize: 100,
          meetingChatPersistent: true
        }
      };

      return tierLimits[tier] || tierLimits.free;
    };

    const limits = getLimits();

    // Check if user can create a new group chat
    const canCreateGroupChat = (currentGroupCount = 0) => {
      if (!canUseGroupChats) return { allowed: false, reason: 'Upgrade to Pro to create group chats' };

      if (limits.activeGroupChats === -1) {
        return { allowed: true, reason: null };
      }

      if (currentGroupCount >= limits.activeGroupChats) {
        return {
          allowed: false,
          reason: `You've reached the limit of ${limits.activeGroupChats} active group chats. Upgrade to Team for unlimited groups.`
        };
      }

      return {
        allowed: true,
        remaining: limits.activeGroupChats - currentGroupCount
      };
    };

    // Check if file type is allowed
    const isAllowedFileType = (mimeType) => {
      return ALLOWED_FILE_TYPES.includes(mimeType);
    };

    // Check if file can be shared
    const canShareFile = (fileSizeMB, mimeType = null) => {
      if (!canShareFiles) {
        return { allowed: false, reason: 'File sharing is not available for your tier' };
      }

      // Check file type if provided
      if (mimeType && !isAllowedFileType(mimeType)) {
        return {
          allowed: false,
          reason: 'File type not allowed. Please upload images (JPEG, PNG, GIF), videos (MP4), PDFs, or Word documents.'
        };
      }

      if (fileSizeMB > limits.chatFileSize) {
        return {
          allowed: false,
          reason: `File size exceeds ${limits.chatFileSize}MB limit.${tier === 'free' ? ' Upgrade to Pro for 100MB uploads.' : ''}`
        };
      }

      return { allowed: true };
    };

    // Get upgrade prompt for a feature
    const getUpgradePrompt = (feature) => {
      const prompts = {
        groupChats: {
          title: 'Unlock Group Chats',
          description: 'Create group conversations with your team. Upgrade to Pro to get started.',
          requiredTier: 'pro'
        },
        orgChannels: {
          title: 'Organization Channels',
          description: 'Get dedicated channels for your team with the Team plan.',
          requiredTier: 'team'
        },
        chatFileSharing: {
          title: 'Share Files in Chat',
          description: 'Send documents, images, and files in your conversations.',
          requiredTier: 'pro'
        },
        chatSearch: {
          title: 'Search Chat History',
          description: 'Find past messages quickly with search. Upgrade to Pro.',
          requiredTier: 'pro'
        },
        persistentMeetingChat: {
          title: 'Keep Meeting Chat History',
          description: 'Pro and Team users can access meeting chat after the meeting ends.',
          requiredTier: 'pro'
        }
      };

      return prompts[feature] || null;
    };

    return {
      // Feature checks
      canUseDMs,
      canUseMeetingChat,
      hasPersistentMeetingChat,
      canUseGroupChats,
      canUseOrgChannels,
      canShareFiles,
      canSearchChats,

      // Limits
      limits,
      chatHistoryDays: limits.chatHistoryDays,
      maxGroupChats: limits.activeGroupChats,
      maxFileSize: limits.chatFileSize,

      // Functions
      canCreateGroupChat,
      canShareFile,
      isAllowedFileType,
      getUpgradePrompt,

      // Constants
      ALLOWED_FILE_TYPES,

      // Context
      tier,
      isTeam,
      hasOrganization,
      organizationId,
      userId: user?.uid,
      userName: userProfile?.displayName || user?.displayName || 'Anonymous'
    };
  }, [tier, hasFeature, checkLimit, isTeam, hasOrganization, organizationId, user, userProfile]);

  return permissions;
}

export default useChatPermissions;
